import React, { createContext, useContext, useState, useEffect } from "react";
import authService from "../services/authService";

// Create Auth Context
const AuthContext = createContext({
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
  login: () => {},
  logout: () => {},
  updateUser: () => {},
  refreshUser: () => {},
});

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize auth state on mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const storedToken = authService.getToken();
        const storedUser = authService.getStoredUser();

        if (storedToken && storedUser) {
          setToken(storedToken);
          setUser(storedUser);

          // Optionally verify token with backend
          try {
            const response = await authService.getCurrentUser();
            if (response.success) {
              setUser(response.data);
            } else {
              // Token might be expired, clear auth
              authService.clearAuth();
              setUser(null);
              setToken(null);
            }
          } catch (error) {
            console.warn("Failed to verify token on startup:", error);
            // Keep using stored data for now
          }
        }
      } catch (error) {
        console.error("Error initializing auth:", error);
        authService.clearAuth();
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Login function
  const login = async (email, password) => {
    try {
      setIsLoading(true);
      const response = await authService.login(email, password);

      if (response.success) {
        setUser(response.data);
        setToken(response.token);
        return { success: true, message: response.message };
      } else {
        return { success: false, message: response.message };
      }
    } catch (error) {
      console.error("Login error in context:", error);
      return {
        success: false,
        message: "An unexpected error occurred during login",
      };
    } finally {
      setIsLoading(false);
    }
  };

  // Logout function
  const logout = async () => {
    try {
      setIsLoading(true);
      await authService.logout();
      setUser(null);
      setToken(null);
      return { success: true, message: "Logged out successfully" };
    } catch (error) {
      console.error("Logout error in context:", error);
      // Still clear local state even if backend call fails
      authService.clearAuth();
      setUser(null);
      setToken(null);
      return { success: true, message: "Logged out successfully" };
    } finally {
      setIsLoading(false);
    }
  };

  // Update user data
  const updateUser = (userData) => {
    setUser((prevUser) => ({
      ...prevUser,
      ...userData,
    }));
    // Update localStorage as well
    localStorage.setItem(
      "user",
      JSON.stringify({
        ...user,
        ...userData,
      })
    );
  };

  // Refresh user data from backend
  const refreshUser = async () => {
    try {
      const response = await authService.getCurrentUser();
      if (response.success) {
        setUser(response.data);
        return { success: true, data: response.data };
      } else {
        return { success: false, message: response.message };
      }
    } catch (error) {
      console.error("Refresh user error:", error);
      return { success: false, message: "Failed to refresh user data" };
    }
  };

  const contextValue = {
    user,
    token,
    isAuthenticated: !!user && !!token,
    isLoading,
    login,
    logout,
    updateUser,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// HOC for protected routes
export const withAuth = (Component) => {
  return function AuthenticatedComponent(props) {
    const { isAuthenticated, isLoading } = useAuth();

    if (isLoading) {
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            backgroundColor: "#000",
            color: "white",
          }}
        >
          Loading...
        </div>
      );
    }

    if (!isAuthenticated) {
      // Redirect to login or show unauthorized
      window.location.href = "/login";
      return null;
    }

    return <Component {...props} />;
  };
};

export default AuthContext;
